import { vec2 } from "@tlaukkan/tsm";
import { Entity, World } from "ecsy";
import { FrameMemoryArena } from "../../shared/box2dUtils";
import { registerCommonSystemsPostPhysics, registerCommonSystemsPrePhysics } from "../../shared/common";
import { Flashing, Placeable, PreviewOnly, Transform } from "../../shared/components";
import { CompositionElement, instantiateCompElement, lookupEntitySize } from "../../shared/composition";
import { Camera, CameraZoomLevel, Multiplayer, PhysicsConstants, RenderEffects, Resolution, ShopConstants, SimulationConstants, Speeds, SteamAchievements, UIConstants } from "../../shared/constants";
import { Debug } from "../../shared/debug";
import { clearEntityFactory, collectEntityGarbage, destroyEntity, getEntityById, getId } from "../../shared/entityFactory";
import { Game, GameMode } from "../../shared/game";
import { GameElement } from "../../shared/gameElementTypes";
import { BuyAntibodyAction, ClientAction, SellAntibodyAction, ServerMessage, SimulationSyncMessage, StartGame, SyncMessageType, VirusGrowthType } from "../../shared/messageTypes";
import { Player } from "../../shared/player";
import { Sound } from "../../shared/sound";
import { AnimationSystem, wait } from "../../shared/systems/animationSystem";
import { StateSystem } from "../../shared/systems/stateSystem";
import { addComponent, getSystem, metersToPixels, uuidToBase64, executesEvenIfPaused } from "../../shared/utils";
import { App } from "./components/App";
import { registerBugReportContextProvider } from "./bugReport/bugReporter";
import { getVersionString } from "../../shared/version";
import { ConnectionToServer } from "./networking/connectionToServer";
import { ClientReconnectingState } from "./states/clientReconnectingState";
import { MessageBuffer } from "./messageBuffer";
import { RenderEngine } from "./rendering/renderEngine";
import { ClientLobbyState } from "./states/clientLobbyState";
import { ClientShopState } from "./states/clientShopState";
import { CameraSystem } from "./systems/cameraSystem";
import { ClientSyncSystem } from "./systems/clientSyncSystem";
import { ClientTargetingSystem } from "./systems/clientTargetingSystem";
import { EntityVFXSystem } from "./systems/entityVFXSystem";
import { HexTileSystem } from "./systems/hexTileSystem";
import { OverlayInfoSystem } from "./systems/overlayInfoSystem";
import { ParticleSystem } from "./systems/particleSystem";
import { PlacementSystem } from "./systems/placementSystem";
import { RendererSystem } from "./systems/renderSystem";
import { SoundSystem } from "./systems/soundSystem";
import { getIngameTutorial, getUserId, resetTutorials, getLadderRank, getUserSettings, setTrainingLevel } from "./userSettings";
import { recordGameStarted } from "./userStats";
import { gameModeToName, HostTransport } from "../../shared/replayStats";
import { TutorialStateMachine } from "./tutorialStateMachine";
import { TutorialId, TutorialStep } from "./components/TooltipInfo";
import { TrainingLevel } from "../../shared/playerAccount";
import { PointOfInterestSystem } from "./systems/pointOfInterestSystem";
import { lookupGameElementShapeAndSize } from "../../shared/gameElement";
import { IngameTutorialStep } from "./tutorialGraph";
import { chooseOpponent, hasTutorialReplay, reportReplacedSaveReplays, reportReplay } from "./networking/replaySharing";
import { SoundEngine } from "./sound/soundEngine";
import * as AchievementTracker from "./AchievementTracker";
import { recordEarn, recordSpend, resetGameStats } from "./gameStats";
import { ConnectionToRemotePeer, SteamConnectionToRemotePeer, WebRTCConnectionToRemotePlayer, wireRemotePeerToServerConnection } from "./networking/connectionToRemotePeer";
import { HostReconnect } from "./networking/hostReconnect";
import { PlayTimer } from "./playTimer";

export type ImmuneCellInspection = {
	element: GameElement,
	position: vec2,
	compElement?: CompositionElement,
	hideTooltips?: boolean
}

export type RoundRewardDisplay = {
	experience: number,
	leftScore: number,
	rightScore: number,
	playerIsLeft: boolean,
	earned: boolean | null,
}

export class GameClient extends Game {
	app: App;
	world: World;
	renderEngine!: RenderEngine;
	soundEngine!: SoundEngine;

	playerID: number = -1;
	playerName: string = '';
	round: number = 0;
	lastTime: number = 0;
	timeTillNextFrame = 0;
	frameDeltaTime = -1;
	connectionToServer!: ConnectionToServer | null;
	listeners = new Array<(msg: SimulationSyncMessage) => void>();
	farthestStepNumber = -1;
	stateSystem!: StateSystem;
	cameraSystem!: CameraSystem;
	messageBuffer!: MessageBuffer | null;
	shouldUpdateUI = false;
	buffering = false;
	paused = 0;
	winners: Array<number> | null = null;
	inspection: ImmuneCellInspection | null = null;
	gainedMoney = 0;
	hideMarket: boolean = false;
	disableMarket: boolean = false;
	showVirusGrowthDisplay = false;
	showTiebreakerAnnouncement = false;
	tiebreakerRound = 0;
	showOpponentName = "";
	onConfirmVirusGrowth: () => void = () => { };
	focusedOnCells = true;
	gameTakesKeyEvents = true;
	gameStartTime = -1;
	hoveredEntity: number | null = null;
	isCurrentlyDragging: boolean = false;

	isConnected = false;
	isConnecting = false;
	connectCallback: ((isReconnecting: boolean) => void) | null = null;

	allowTutorial = false;
	showTutorial: boolean = true;
	seenTutorials: Set<TutorialId> = new Set<TutorialId>();

	queueContinueGame: string | null = null;

	trainingLevel: TrainingLevel = TrainingLevel.Trained;
	trainingLevelUpdated = false;
	tutorialsThisGame: Set<IngameTutorialStep> = new Set();

	isElectron = false;
	tutorialStateMachine = new TutorialStateMachine(this);

	autoCameraActive = false;
	enabledButtons?: string;

	roundRewardDisplay: RoundRewardDisplay | null = null;

	isRankedGame = false;
	opponentRank: number | null = null;

	playTimer = new PlayTimer();

	gameId: number | null = null;
	private nextGameId = 1;

	playerHasEndedTurn = false
	hostId: string | undefined;
	peerConnected = false;
	hostReconnect: HostReconnect | null = null;
	isSpawnGame = false;
	opponentDisconnected = false;
	signallingServerLobbyCode: string | null = null;
	private remoteConnection: ConnectionToRemotePeer | null = null;

	rerollNotication: boolean = false;

	frameLoopRequestId: number | null = null;
	frameLoopTimeoutId: any = null;

	crashReport: { text: string, canReloadSave: boolean } | null = null;
	continueOnLobby = false;

	constructor(app: App) {
		super();
		this.app = app;

		console.log("USER AGENT: " + navigator.userAgent)
		if (navigator.userAgent.includes("Electron")) {
			this.isElectron = true;
			console.log("Running in Electron");
			console.log((window as any).electronApi);
		}

		//This gets recreated in connect(), but gameClient logic assumes this always exists so define a dummy first
		this.world = new World();

		registerBugReportContextProvider(() => this.getBugReportContext());
	}



	initializeGraphicsAndSound(canvas: HTMLCanvasElement, uiRegion: HTMLElement) {
		this.renderEngine = new RenderEngine(canvas, uiRegion);
		this.soundEngine = new SoundEngine(this);
		this.soundEngine.init();
	}

	async copyTextToClipboard(text: string) {
		// TODO: clipboard api fallback
		try {
			await navigator.clipboard.writeText(text)
			console.log('Copying to clipboard was successful');
		} catch (e) {
			console.log('Copying to clipboard was not successful');
		}
	}

	reset() {
		super.reset();
		this.stopMainLoop();
		this.playerID = -1;
		this.round = 0;

		this.lastTime = 0;
		this.timeTillNextFrame = 0;
		this.farthestStepNumber = -1;
		this.shouldUpdateUI = false;
		this.buffering = false;
		this.winners = null;
		this.setPaused(0);
		this.gameStartTime = -1;
		this.tutorialStateMachine = new TutorialStateMachine(this);
		this.tutorialsThisGame = new Set();
		this.isRankedGame = false;
		this.opponentRank = null;
		this.playTimer = new PlayTimer();
		this.playerHasEndedTurn = false;
		this.isConnected = false;
		this.isConnecting = false;
		this.peerConnected = false;
		this.hostReconnect?.cancel(); //an attempt in flight must not report back into a dead session
		this.hostReconnect = null;
		this.opponentDisconnected = false;
		this.signallingServerLobbyCode = null;
		//this.isSpawnGame = false; //Intentionally not reset, this only happens on the client only join path where you need a host no matter what

		this.hideMarket = false;
		this.disableMarket = false;
		//TODO: Double check that all values appropriate to reset are reset

		this.trainingLevel = TrainingLevel.Trained;

		if (process.env.is_standalone && this.messageBuffer) {
			this.messageBuffer.clear();
		}

		if (this.world) {
			this.world.getSystems().forEach((system: any) => {
				system.destroy && system.destroy();
			});
		}

		if (this.connectionToServer) {
			this.connectionToServer.close();
			this.connectionToServer = null;
		}

		if (this.remoteConnection) {
			this.remoteConnection.disconnectAndDispose();
			this.remoteConnection = null;
		}

		if (Debug.ManualSimulationStart) {
			document.onkeydown = (event) => { //MARK FIX THIS
				if (event.key === 'n') {
					event.preventDefault();
					console.log("Forcing Simulation start");
					this.sendForceSimulation();
				} else if (event.key === 'p') {
					event.preventDefault();
					this.saveState("debugsave");
				} else if (event.key === 'l') {
					this.sendLoadSimulation("debugsave");
				}

				//Debug key for testing round reward animation
				// if (event.key === 'e' && this.roundRewardDisplay) {
				// 	this.roundRewardDisplay.earned = true;
				// 	this.updateUI();
				// }

				// if (this.gameTakesKeyEvents) event.preventDefault();
			};
		}
	}

	setTrainingLevel(level: TrainingLevel) {
		this.trainingLevel = level;
	}

	async connect(connection: ConnectionToServer) {
		//The last game is only cleared away (sprites and all) when the world runs. Usually the lobby has run plenty of
		//frames by now, but not if a game is started right as we return to it (eg reloading the save after a crash)
		if (this.world) {
			try {
				this.world.execute(0, performance.now());
			}
			catch (error) {
				console.warn("GameClient: The last game's world failed to clear", error);
			}
		}
		this.reset();


		this.connectionToServer = connection;
		this.isSpawnGame = connection.isSecondPlayer;
		this.connectionToServer.addCloseListener((intentional) => {
			if (!intentional) {
				this.onServerConnectionLost();
			}
		});
		this.messageBuffer = new MessageBuffer(this.connectionToServer);

		this.isConnecting = true;
		await this.renderEngine.init();

		if (this.isSpawnGame) {
			this.app.forceUpdate();
		}

		this.world = new World();
		this.world
			.registerSystem(ClientSyncSystem, { messageBuffer: this.messageBuffer, game: this })
			.registerSystem(AnimationSystem)

		registerCommonSystemsPrePhysics(this.world, this)
			.registerSystem(ClientTargetingSystem, { game: this })
			.registerSystem(PointOfInterestSystem, { game: this }); //just clears pois at the start of the frame
		registerCommonSystemsPostPhysics(this.world, this)
			.registerSystem(HexTileSystem, { game: this })
			.registerSystem(RendererSystem, { game: this })
			.registerSystem(EntityVFXSystem, { game: this })
			.registerSystem(OverlayInfoSystem, { game: this })
			.registerSystem(CameraSystem, { game: this })
			.registerSystem(ParticleSystem, { game: this })
			.registerSystem(SoundSystem, { game: this })
			.registerSystem(PlacementSystem, { game: this })

		this.stateSystem = getSystem(this.world, StateSystem);
		this.cameraSystem = getSystem(this.world, CameraSystem);

		this.stateSystem.pushState(new ClientLobbyState(this));
		this.connectionToServer!.sendConnectMessage();
	}

	waitForConnection(timeoutMs: number = Multiplayer.ServerAckTimeout) {
		if (this.isConnected) return Promise.resolve({ isReconnecting: false });
		if (this.connectCallback) {
			console.error("GameClient: waitForConnection called while already waiting for the server");
			return Promise.resolve(null);
		}

		return new Promise<{ isReconnecting: boolean } | null>((resolve, _reject) => {
			const timer = setTimeout(() => {
				this.connectCallback = null;
				console.error(`GameClient: No acknowledge from the server after ${timeoutMs}ms`);
				//half open: we are talking to something that never answered, so drop it
				if (this.connectionToServer) {
					this.connectionToServer.close();
					this.connectionToServer = null;
				}
				this.isConnecting = false;
				this.isConnected = false;
				resolve(null);
			}, timeoutMs);

			this.connectCallback = (isReconnecting) => {
				clearTimeout(timer);
				this.connectCallback = null;
				resolve({ isReconnecting });
			};
		});
	}

	setRemotePlayerConnection(remoteConnection: ConnectionToRemotePeer) {
		if (!this.connectionToServer) {
			console.error("GameClient: Cannot set remote player connection without a server connection");
			return false;
		}
		this.remoteConnection = remoteConnection;
		this.peerConnected = true;
		if (remoteConnection instanceof WebRTCConnectionToRemotePlayer) {
			const rawId = remoteConnection.signallingServerLobbyCode;
			this.signallingServerLobbyCode = rawId ? encodeURIComponent(uuidToBase64(rawId)) : null;
		}
		this.updateUI();

		this.hostReconnect = new HostReconnect(remoteConnection, peerConnected => {
			this.peerConnected = peerConnected;
			this.updateUI();
		});
		remoteConnection.addCloseListener(intentional => this.hostReconnect?.onClosed(intentional));

		wireRemotePeerToServerConnection(remoteConnection, this.connectionToServer);
		return true;
	}

	onServerConnectionLost() {
		if (this.getCurrentState() === 'reconnecting') return;
		if (this.getCurrentState() === 'lobby') return;

		console.log('Connection lost mid-game, entering reconnect mode');
		this.isConnected = false;
		this.goToReconnect(false)
	}

	//TODO: Make a "ClientMessaageCenter" that has all these shortcut methods

	previewBuyAntibody(type: GameElement, index: number, isReward: boolean) {
		const state = this.stateSystem.peekState()
		if (state.getName() === "shop") {
			(state as ClientShopState).previewBuyCell(type, index, isReward);
		}
	}

	onCombine(player: Player, element: CompositionElement): void {
		if (!this.isSpawnGame && getIngameTutorial()) {
			if (player === this.getPlayer()) {
				this.tutorialStateMachine.onCombineCell(element);
				this.updateUI();
			}
		}
	}

	onPromote(player: Player, comp: CompositionElement) {
		//Since SAP style triple change there is only one promoted ID at a time in practice
		this.doPromoteEffect(comp, player.id, ShopConstants.PromotionPopDelay);
		wait(ShopConstants.PromotionPopDelay).then(() => {
			getSystem(this.world, SoundSystem).playSound(Sound.Promote, new vec2([0, 0]), false);
			this.updateUI();
		});
	}

	async doPromoteEffect(element: CompositionElement, playerId: number, delay: number) {
		const entity = getEntityById(element.id);
		if (!entity) return;

		entity.removeComponent(Placeable);

		const scale = ShopConstants.PromotionPopScale;
		await getSystem(this.world, EntityVFXSystem).squashAndStretchSprite(entity, new vec2([scale, scale]));

		const stateSystem = getSystem(this.world, StateSystem);

		//Must be careful to avoid messing with entities if state has changed
		if (stateSystem.peekState().getName() !== "shop") return;

		destroyEntity(entity, true);

		//await wait(1);

		// //Must be careful to avoid messing with entities if state has changed
		// if (stateSystem.peekState().getName() !== "shop") return;

		instantiateCompElement(element, playerId);
	}

	chooseReward(index: number) {
		const player = this.getPlayer()
		if (player && this.connectionToServer) {
			const action = {
				messageType: ClientAction.ChooseReward,
				index
			}
			this.connectionToServer.send(action);
			player.rewardOffer.push(player.rewardOptions[index]);
			player.rewardOptions = [];
			if (!this.isSpawnGame && getIngameTutorial()) {
				this.tutorialStateMachine.onRewardCell();
			}
			this.updateUI();
		}
	}

	sendBuyAntibody(index: number, isReward: boolean, type: GameElement, requestedPosition?: vec2) {
		if (!requestedPosition) {
			const player = this.getPlayer()!;
			const placementSystem = getSystem(this.world, PlacementSystem);
			const { shapeType, size } = lookupGameElementShapeAndSize(type); //Ensure the type is valid
			requestedPosition = placementSystem.findNearestValidPlacement({
				position: new vec2(player.spawnPoint),
				rotation: 0,
				size,
				shape: shapeType,
			}, type, true);
		}
		const action: BuyAntibodyAction = {
			messageType: ClientAction.BuyAntibody,
			index,
			isReward,
			requestedPosition: requestedPosition.xy
		};
		this.connectionToServer?.send(action);
	}

	sendSellAntibody(id: number) {
		const action: SellAntibodyAction = {
			messageType: ClientAction.SellAntibody,
			id,
		};
		this.connectionToServer?.send(action);
	}

	// Sells hovered immune cell
	sellImmuneCell() {
		const draggedEntity = getSystem(this.world, PlacementSystem).claimDraggedEntityForSell();
		if (draggedEntity === undefined || draggedEntity === null) {
			console.warn("No entity to sell");
			return;
		}

		const transform = draggedEntity.getComponent(Transform);
		const pos = transform ? transform.position.copy() : new vec2(this.getPlayer()!.spawnPoint);
		const id = getId(draggedEntity);
		if (!id) throw new Error('Tried to sell non existant id');

		this.sendSellAntibody(id);
		getSystem(this.world, SoundSystem).playSound(Sound.SellChime, pos);
		getSystem(this.world, ParticleSystem).createUIEffect(pos);
		this.hoveredEntity = null;
	}

	sendBuyExperience() {
		const action = {
			messageType: ClientAction.BuyExperience,
		};
		this.connectionToServer?.send(action);
		this.tutorialStateMachine.onBuyExperience();
	}

	sendRefreshShop() {
		const action = {
			messageType: ClientAction.RefreshShop,
		};
		this.connectionToServer?.send(action);
	}

	sendFreezeShop() {
		const action = {
			messageType: ClientAction.FreezeShop,
		};
		this.connectionToServer?.send(action);
	}

	sendSetName(name: string) {
		const action = {
			messageType: ClientAction.SetName,
			name,
			trainingLevel: this.trainingLevel
		};
		this.connectionToServer?.send(action);
	}

	playerEndRound() {
		if (this.getCurrentState() === "shop") {
			const action = {
				messageType: ClientAction.EndTurn
			}
			this.connectionToServer?.send(action);
			this.playerHasEndedTurn = true;
		}
	}

	sendForceSimulation() {
		const action = {
			messageType: ClientAction.DebugForceSimulaton
		}
		this.connectionToServer?.send(action);
	}

	saveState(name: string) {
		console.log("Serializing GameState");
		const saveRequest = {
			messageType: ClientAction.SaveGame,
			gameName: name,
		}

		this.connectionToServer?.send(saveRequest);
		// const serialized = JSON.stringify(gameState);
	}

	onDataSaved(name: string, saveData: string, type: "saves" | "replays") {
		//this.copyTextToClipboard(serialized);
		//console.log("Copied gameState to clipboard");
		if (type === "saves") {
			saveData = this.modifyAndReportSave(name, saveData);
		}
		try {
			localStorage.setItem(name, saveData);
			console.log(`Stored ${name} in localStorage`);
		}
		catch (error) {
			console.error(`Could not store ${name} in localStorage`, error); //eg storage is full, the game carries on without it
		}
		//The host uploads every player's replay, so a joined game (Steam or browser) never uploads
		if (type === 'replays' && name !== 'debug_last_replay' && !this.isSpawnGame) {
			reportReplay(saveData, {
				isOwnReplay: name !== 'other_player_replay',
				opponentRank: this.opponentRank,
				playTimeMs: this.playTimer.getMs(),
				hostTransport: this.getHostTransport(),
			});
		}
	}

	/** Only known to the host, while the other player is connected */
	private getHostTransport(): HostTransport | null {
		return this.remoteConnection instanceof SteamConnectionToRemotePeer ? 'steam' :
			this.remoteConnection instanceof WebRTCConnectionToRemotePlayer ? 'browser' :
				null;
	}

	private modifyAndReportSave(name: string, saveData: string) {
		try {
			const save = JSON.parse(saveData);
			const hostTransport = this.getHostTransport();
			if (hostTransport) save.hostTransport = hostTransport; //solo games have nothing to record
			if (this.opponentRank !== null) save.opponentRank = this.opponentRank;
			save.playTimeMs = this.playTimer.getMs();
			if (!this.isSpawnGame && save.gameId) {
				reportReplacedSaveReplays(localStorage.getItem(name), save.gameId);
			}
			return JSON.stringify(save);
		}
		catch (error) {
			console.error("Could not add this client's data to the save, storing it as sent", error);
			return saveData;
		}
	}

	sendLoadSimulation(name: string) {
		const data = localStorage.getItem(name);
		const loadRequest = {
			messageType: ClientAction.LoadGame,
			gameName: name,
			selfId: this.playerID,
			data: data || undefined
		}
		this.connectionToServer?.send(loadRequest);
	}

	async sendStartGame(mode: GameMode, continueGame: string | null = null) {
		const gameId = this.gameId;
		if (gameId === null) {
			console.error("GameClient: sendStartGame called without newGame");
			return;
		}
		this.queueContinueGame = continueGame;
		const savedStats = continueGame === null ? null : readSavedClientStats(continueGame);
		this.opponentRank = savedStats?.opponentRank ?? null;
		this.playTimer.start(savedStats?.playTimeMs ?? 0);
		if (this.trainingLevel === TrainingLevel.MultiplayerIntro && !hasTutorialReplay()) {
			//Shouldn't happen since beating SecondGame saves it, but local data may have been cleared
			console.warn("No tutorial replay for MultiplayerIntro opponent, redoing SecondGame");
			this.trainingLevel = TrainingLevel.SecondGame;
			setTrainingLevel(this.trainingLevel);
		}
		if (this.trainingLevel === TrainingLevel.MultiplayerIntro) {
			mode = GameMode.Asynchronous;
		}
		//A continued game was counted when it first started
		if (continueGame === null) {
			recordGameStarted(gameModeToName(mode, this.trainingLevel));
		}
		let requiredPlayerCount = mode === GameMode.Solo ? 1 : 2;
		if (Debug.ForceNumberOfPlayers) requiredPlayerCount = Debug.ForceNumberOfPlayers;

		const botReplays: StartGame['botReplays'] = []
		if (mode === GameMode.Asynchronous) {
			//A continued game already has its opponent in the save
			if (continueGame === null) {
				const oneAndOnlyBot = await chooseOpponent(this);
				//Abandoned while choosing, and maybe already replaced by another game, which sends its own StartGame
				if (this.gameId !== gameId) {
					console.log("GameClient: Game start was cancelled while choosing an opponent");
					return;
				}
				botReplays.push({ name: oneAndOnlyBot.name, data: oneAndOnlyBot.data, replayID: oneAndOnlyBot.replayID });
				this.opponentRank = oneAndOnlyBot.opponentRank;
			}
			//The tutorial's replay opponent doesn't count towards the ladder
			this.isRankedGame = this.trainingLevel === TrainingLevel.Trained;
		}
		const action: StartGame = {
			messageType: ClientAction.StartGame,
			trainingLevel: this.trainingLevel,
			requiredPlayers: requiredPlayerCount,
			botReplays
		}
		this.requiredPlayers = requiredPlayerCount;
		this.connectionToServer?.send(action);
		this.gameStartTime = Date.now();

		AchievementTracker.onStartGame(this, mode);
		resetGameStats();
	}

	newGame() {
		if (this.gameId !== null) {
			console.warn(`GameClient: Game ${this.gameId} already in progress, not starting another`);
			return false;
		}
		this.gameId = this.nextGameId++;
		this.setUIState(); //the lobby disables its start buttons
		return true;
	}

	endGame() {
		//Always leave, the connections may be up without a game claimed
		this.leaveMultiplayerSession();
		if (this.gameId === null) return;
		this.gameId = null;
		this.setUIState(); //the lobby enables its start buttons again
	}

	sendDragTrigger(id: number, currentPosition: vec2) {
		const action = {
			messageType: ClientAction.DragTrigger,
			id,
			toPosition: currentPosition.xy
		}
		this.connectionToServer?.send(action);
	}

	async showPlayer(playerId: number) {
		const player = this.players.find(player => player.id === playerId);
		if (player) {
			const cameraSystem = getSystem(this.world, CameraSystem);
			await cameraSystem.focusOnPlayer(player);
		}
	}

	getCurrentState() {
		return this.stateSystem?.getCurrentState() || "lobby";
	}

	getCurrentRound() {
		return this.round;
	}

	getPlayer() {
		return this.players.find(player => player.id === this.playerID);
	}

	getWinners() {
		return this.winners?.map(winnerIndex => this.players[winnerIndex]);
	}

	setWinners(winners: Array<number>) {
		this.winners = winners;
	}

	setInspection(inspect: ImmuneCellInspection | null) {
		if (inspect === null && (Debug.KeepInspection || this.tutorialStateMachine.forceCellDescriptionShown)) return;
		if (this.inspection === inspect) return;
		if (inspect && this.inspection && inspect.element === this.inspection.element) return;
		this.inspection = inspect;
		if (!this.isSpawnGame && getIngameTutorial() && inspect !== null) {
			this.tutorialStateMachine.onInspectCell(inspect.element);
		}
		this.updateUI();
	}

	isInspectionBlockingCell() {
		return this.inspection
			&& this.inspection.position.x !== 0
			&& this.inspection.position.y !== 0
			&& this.inspection.position.x < Resolution.Width / 2
			&& this.inspection.position.y < Resolution.Height / 2;
	}

	setHideMarket(hide: boolean) {
		if (this.disableMarket) return;
		const change = this.hideMarket !== hide;
		if (change) {
			this.hideMarket = hide;
			this.updateUI();
		}
	}

	async showVirusGrowth(on: boolean) {
		return new Promise<void>(async (resolve, _) => {
			const player = this.getPlayer();
			let pos = new vec2([0, 0]);
			let type = GameElement.VIRUS;
			if (player && on && this.latestVirusGrowth) {
				let affectedSpawners: CompositionElement[] = [];
				const waves = this.getVirusWaves(player);
				if (this.latestVirusGrowth.some(growth => growth.damageRequired > 0)) {
					for (const wave of waves) {
						for (const cell of wave.cells) {
							if (cell.entityParameters?.hasTiebreakerGrowth) {
								affectedSpawners.push(cell);
							}
						}
					}
				}
				for (const growth of this.latestVirusGrowth) {
					const thisWave = waves[growth.wave];
					if (!thisWave) continue;
					affectedSpawners = thisWave.cells;

					for (const spawner of affectedSpawners) {
						const entity = getEntityById(spawner.id)
						if (spawner.entityParameters?.spawnWaveCenter) {
							pos.xy = spawner.entityParameters?.spawnWaveCenter;
							type = spawner.entityParameters?.spawnTargetType ?? GameElement.VIRUS;
						}
						if (entity) {
							const flashTimeTotal = 2;
							const flashDuration = 0.2;
							addComponent(entity, Flashing, { lengthSec: flashDuration, times: Math.floor(flashTimeTotal / flashDuration) })
						}
					}
				}
			}

			//Move camera, start in right place if beginning of game, animate there otherwise
			if (this.round === 1) {
				this.cameraSystem.cameraSpeed = Speeds.CameraNormalSpeed;
				this.cameraSystem.setPositionInstant(pos);
				this.cameraSystem.setAperatureFocus(true, RenderEffects.VignetteApertureFocusedSmall, 0.3);
			}
			else {
				this.cameraSystem.cameraSpeed = Speeds.CameraNormalSpeed * 2;
				this.cameraSystem.focusOnPosition(pos);

				const timeForCameraMove = metersToPixels(Camera.StepMeters) * this.cameraSystem.getFinalCameraSpeed();
				this.cameraSystem.setAperatureFocus(true, RenderEffects.VignetteApertureFocusedSmall, timeForCameraMove / 1000);
			}

			await this.cameraSystem.setZoomLevel(CameraZoomLevel.Medium);

			this.cameraSystem.cameraSpeed = Speeds.CameraVerySlowSpeed;
			this.cameraSystem.controlsLocked = true;

			if (!this.isSpawnGame && getIngameTutorial()) {
				await this.tutorialStateMachine.waitForTutorialEndpoint();
			}

			this.setInspection({
				element: type,
				position: vec2.zero.copy(),
				hideTooltips: true
			})

			if (!this.isSpawnGame && getIngameTutorial()) {
				this.tutorialStateMachine.onRecapVirusGrowthNotice();
				await this.tutorialStateMachine.waitForTutorialEndpoint();
				this.updateUI();
			}

			this.showVirusGrowthDisplay = on;
			this.onConfirmVirusGrowth = () => {
				if (this.tutorialStateMachine.tutorialWantsClicks()) return;
				this.showVirusGrowthDisplay = false;
				this.setInspection(null);
				this.updateUI();
				resolve();
			};
			this.updateUI();
		});
	}

	gainMoneyAnim: any = null;
	onGainMoney(player: Player, amount: number) {
		if (player !== this.getPlayer()) return;
		if (amount > 0) {
			recordEarn(amount);
		}
		else if (amount < 0) {
			recordSpend(-amount);
		}
		this.gainedMoney += amount;
		this.updateUI();
		if (amount !== 0) { //negative gain still counts
			clearTimeout(this.gainMoneyAnim);
			this.gainMoneyAnim = setTimeout(() => {
				this.gainedMoney = 0;
				this.updateUI();
			}, UIConstants.GainMoneyAnimationSeconds * 1000);
		}

	}

	setVolume(value: number) {
		if (this.world) {
			if (this.soundEngine) {
				this.soundEngine.setVolume(value);
			}
			return true;
		} else {
			return false;
		}
	}

	setSFXVolume(value: number) {
		if (this.world) {
			if (this.soundEngine) {
				this.soundEngine.setSFXVolume(value);
			}
			return true;
		} else {
			return false;
		}
	}

	setFreqVolume(value: number) {
		if (this.world) {
			if (this.soundEngine) {
				this.soundEngine.setFreqVolume(value);
			}
			return true;
		} else {
			return false;
		}
	}

	setMusicVolume(value: number) {
		if (this.world && !Debug.CaptureMode) {
			if (this.soundEngine) {
				this.soundEngine.setMusicVolume(value);
			}
			return true;
		} else {
			return false;
		}
	}

	setDontShowTutorial(value: boolean) {
		this.showTutorial = !value;
	}

	setZoomLevel(level: CameraZoomLevel) {
		this.cameraSystem.setZoomLevel(level);
	}

	resetTutorials() {
		resetTutorials();
	}

	toggleMute() {
		if (this.world) {
			if (this.soundEngine) {
				this.soundEngine.toggleMute();
			}
			return true;
		} else {
			return false;
		}
	}

	toggleFullscreen() {
		if (window && (window as any).electronApi) {
			(window as any).electronApi.toggleFullscreen();
		}
	}

	saveFileToDisk(name: string, fileContents: string) {
		if (window && (window as any).electronApi) {
			(window as any).electronApi.saveFile(name, fileContents);
		}
	}

	loadFileFromDisk(fileName: string, type = 'utf8') {
		if (window && (window as any).electronApi) {
			return (window as any).electronApi.loadFile(fileName, type);
		}
	}

	loadOfflineReplayFromDisk(rank: number): string | undefined {
		if (window && (window as any).electronApi) {
			return (window as any).electronApi.loadOfflineReplay(rank);
		}
	}

	quit() {
		this.sendTelemetry();
		if (window && (window as any).electronApi) {
			(window as any).electronApi.quit();
		}
	}

	playUISound(name: Sound) {
		if (!this.world) return;
		if (this.soundEngine) {
			this.soundEngine.playUISound(name);
		}
	}

	toggleAutoCamera(enabled: boolean) {
		this.autoCameraActive = enabled;
		this.updateUI();
	}


	sendTelemetry() {
		if (Debug.EnableAnalytics) {
			if (this.gameStartTime === -1) return;
			const endTime = Date.now();
			const gameLength = endTime - this.gameStartTime;
			const payload = JSON.stringify({
				user_id: getUserId(),
				length_ms: gameLength,
				test: Debug.DebugAnalytics,
			});
			// TODO: move to env variable
			const xhr = new XMLHttpRequest();
			xhr.open('POST', 'https://dyrruy4v3l.execute-api.us-east-1.amazonaws.com/Prod', true);
			xhr.setRequestHeader('Content-Type', 'application/json');
			xhr.send(payload);
			this.gameStartTime = -1;
		}
	}

	bufferStartTime = 0;

	startMainLoop() {
		if (this.frameLoopRequestId !== null || this.frameLoopTimeoutId !== null) {
			this.stopMainLoop();
		}
		console.log('starting main game loop client');
		this.run();
	}

	stopMainLoop() {
		if (this.frameLoopRequestId !== null) {
			cancelAnimationFrame(this.frameLoopRequestId);
			this.frameLoopRequestId = null;
		}
		if (this.frameLoopTimeoutId !== null) {
			clearTimeout(this.frameLoopTimeoutId);
			this.frameLoopTimeoutId = null;
		}
	}

	private run = () => {
		if (!this.messageBuffer) {
			return;
		}
		collectEntityGarbage();

		// Compute delta and elapsed time
		const time = performance.now();
		const delta = time - this.lastTime;
		this.frameDeltaTime = delta;
		this.lastTime = time;
		this.timeTillNextFrame -= (delta + 2); //add a little padding

		this.handleCommonMessages();

		//Buffer if sim state needs a message for this step and doesn't have one
		if (!this.messageBuffer.hasCompleteStep()) {
			const state = this.getCurrentState();
			if (state === 'simulation' && this.stateSystem.peekState().isComplete === false && !this.buffering) {
				console.info(`Stopping world to buffer ${performance.now()}, step: ${getStepNumber()}`)
				this.buffering = true;
				this.bufferStartTime = time
			}
		}
		//Stop buffering if we have a messagge
		if (this.buffering && this.messageBuffer.hasCompleteStep()) {
			console.info(`Buffering complete ${time - this.bufferStartTime}, step ${getStepNumber()}`)
			this.buffering = false;
		}

		let didExecuteWorldThisFrame = false;
		if (this.timeTillNextFrame < 0) {

			try {
				// Run all the systems, do a frame of simulation
				if (!this.buffering && !this.paused) {
					this.world.execute(delta, time);
					didExecuteWorldThisFrame = true;
				}

				// Paused games still run the systems that opt in, eg the state system or no one can unpause them
				else if (this.paused) {
					for (const system of this.world.getSystems()) {
						if (!executesEvenIfPaused(system)) continue;
						system.executeEvenIfPaused(delta, time);
						//clearEvents otherwise the next paused frame handles the same added/removed entities again
						(system as unknown as { clearEvents(): void }).clearEvents(); //exists in ecsy, missing from its typings
					}
				}

				this.tutorialStateMachine.update(delta, time);
			}
			catch (error) {
				//Stop for good, show an error
				const errorText = error instanceof Error ? (error.stack ?? error.message) : String(error);
				console.error(`GameClient: The game crashed\n${errorText}`);
				this.showCrashReport(`Crash report: the game crashed\n\n${errorText}`);
				this.stopMainLoop();
				return;
			}

			FrameMemoryArena.clear();
			this.timeTillNextFrame = PhysicsConstants.StepRate * 1000;

			this.frameLoopRequestId = requestAnimationFrame(this.run);
		}
		else {
			//If we have a lot of dead time left in the frame, use a timeout to consume it precisely,
			//so we stay close to Physics.StepRate (eg, 60fps).
			this.frameLoopTimeoutId = setTimeout(this.run, this.timeTillNextFrame);
		}

		if (!didExecuteWorldThisFrame) {
			const animSystem = getSystem(this.world, AnimationSystem);
			if (animSystem) { //one of the few parts of the game this is not guaranteed to exist
				animSystem.execute(delta);
			}
		}

		if (this.shouldUpdateUI) {
			this.setUIState();
			this.shouldUpdateUI = false;
		}
	}

	updateUI() {
		this.shouldUpdateUI = true;
	}

	setUIState() {
		const player = this.getPlayer();
		let zoom = this.cameraSystem ? this.cameraSystem.getZoomLevel() : CameraZoomLevel.Medium;
		this.app.setState({
			player: player || null,
			round: this.round,
			currentFunds: player?.currentFunds || 0,
			maxFunds: player?.maxFunds || 0,
			cells: player?.immuneCells || [],
			hp: player?.hp || 0,
			maxCapacity: player?.maxCapacity || 0,
			offer: player?.offer || [],
			rewardOffer: player?.rewardOffer || [],
			experience: player?.experience || 0,
			opponents: this.players.map(p => [p.hp, p.maxHp, p.level, p.name, p.id, p.immuneCells.length]),
			winners: this.getWinners() || [],
			gainedMoney: this.gainedMoney,
			zoomLevel: zoom,
			draggedImmuneCell: this.isCurrentlyDragging,
			hideMarket: this.hideMarket,
			trainingUpdated: this.trainingLevelUpdated,
			enabledButtons: this.enabledButtons || undefined,
			hostId: this.hostId || undefined,
			isSpawnGame: this.isSpawnGame,
			peerConnected: this.peerConnected,
			isReconnecting: this.getCurrentState() === 'reconnecting',
			hostReconnectFailed: this.hostReconnect?.failed ?? false,
			opponentDisconnected: this.opponentDisconnected,
		});
	}

	handleCommonMessages() {
		const playerSync = this.messageBuffer?.dequeueMessage(SyncMessageType.PlayerSync)
		if (playerSync) {
			this.loadGameState(playerSync.players);
		}

		const ack = this.messageBuffer?.dequeueMessage(SyncMessageType.Acknowledge);
		if (ack) {
			this.playerID = ack.assignedPlayerID;
			if (ack.isReconnection) {
				console.debug("Reconnected to server, reloading game state");
				this.goToReconnect(true);
			}
			else {
				if (this.getCurrentState() === "reconnecting") {
					console.warn("Tried to reconnect to a server with no game, will reset to lobby");
					this.goToLobby(false);
					return;
				}
				console.debug('connected to server');
				this.sendSetName(this.playerName);
			}

			this.isConnected = true;
			this.isConnecting = false;
			if (this.connectCallback) {
				this.connectCallback(ack.isReconnection);
			}
			return;
		}

		const playerConnected = this.messageBuffer?.dequeueMessage(SyncMessageType.PlayerConnected);
		if (playerConnected) {
			this.players = playerConnected.names;
			this.opponentDisconnected = false;
			this.updateUI();
		}

		const playerDisconnected = this.messageBuffer?.dequeueMessage(SyncMessageType.PlayerDisconnected);
		if (playerDisconnected) {
			this.opponentDisconnected = true;
			this.updateUI();
		}

		const serverCrashed = this.messageBuffer?.getUnorderedMessage(SyncMessageType.ServerCrashed);
		if (serverCrashed) {
			this.onServerCrashed(serverCrashed.error);
		}

		const saved = this.messageBuffer?.dequeueMessage(SyncMessageType.DataSaved);
		if (saved) {
			this.onDataSaved(saved.name, saved.data, saved.type);
		}

		const reassignId = this.messageBuffer?.dequeueMessage(SyncMessageType.ReassignPlayerId);
		if (reassignId && this.playerID === reassignId.from) {
			console.log(`Reassigning player id from ${reassignId.from} to ${reassignId.to}`);
			this.playerID = reassignId.to;
			this.connectionToServer?.send({
				messageType: ClientAction.ConfirmReassignedId
			});
		}
	}

	createInfectedCell(player: Player, virusType: GameElement) {
		//No-op, this only works on server
	};

	//Abandons whatever multiplayer session is in progress, in either role. */
	private leaveMultiplayerSession() {
		if (this.remoteConnection) {
			this.remoteConnection.disconnectAndDispose();
			this.remoteConnection = null;
		}
		this.peerConnected = false;
		this.hostReconnect?.cancel(); //an attempt in flight must not report back into a dead session
		this.hostReconnect = null;
		this.opponentDisconnected = false; //no session, so no opponent to be waiting for
		this.hostId = undefined;
		this.signallingServerLobbyCode = null;
		this.updateUI();

		if (this.connectionToServer) {
			this.connectionToServer.close();
			this.connectionToServer = null;
		}
		this.isConnected = false;
		this.isConnecting = false;
	}

	goToLobby(hasAdvanced: boolean) {
		this.endGame();
		//The world has to keep running for the lobby to take over and clear away the game. It may be stopped waiting
		//on a step the server will never send (eg after a crash), and then the old game would stay on screen
		this.buffering = false;
		this.setPaused(0);
		//Drop what the old game still had queued. Left paused, that's the rest of its battle and next shop, which the lobby's
		//world would apply (old sprites) and whose GotoShop the lobby would take for a new game starting
		this.messageBuffer?.clear();

		//The desktop app is a full client that happened to join a game, so it goes back to being
		//an ordinary one. A browser page can only ever have got here from a join link, and exists
		//to serve that one game, so it stays a spawn game.
		if (this.isElectron) {
			this.isSpawnGame = false;
		}
		const state = this.stateSystem.peekState();
		clearEntityFactory();
		this.tutorialStateMachine = new TutorialStateMachine(this); //TODO: Tutorials never seem to to be repeatable.
		if (process.env.is_standalone) {
			for (let i = 0; i < this.stateSystem.statesOnStack(); i++) {
				this.stateSystem.popState();
			}
			this.stateSystem.pushState(new ClientLobbyState(this, hasAdvanced));
		} else {
			this.stateSystem.pushState(new ClientLobbyState(this, hasAdvanced));
			state.isComplete = true;
		}
	}

	private onServerCrashed(error: string) {
		if (this.crashReport !== null) return;
		console.error(`GameClient: The game server crashed\n${error}`);
		this.showCrashReport(`Crash report: the game server crashed\n\n${error}`);
	}

	private showCrashReport(text: string) {
		//The solo autosave is only this game's if this is a solo game that got past the lobby
		const isSoloGame = !this.isSpawnGame && this.players.filter(player => !player.isBot).length <= 1;
		const inGame = this.getCurrentState() !== 'lobby';
		this.crashReport = {
			text,
			canReloadSave: isSoloGame && inGame && localStorage.getItem(Multiplayer.AutosaveName) !== null,
		};
		this.setUIState();
	}

	leaveCrashedGame(reloadSave: boolean) {
		this.crashReport = null;
		this.goToLobby(false); //the server resets once we disconnect
		this.continueOnLobby = reloadSave;
		this.setUIState();
	}

	goToReconnect(hasConnection: boolean) {
		this.setPaused(0); //an announcement waiting on a confirm that never comes would otherwise leave the battle paused for good
		const count = this.stateSystem.statesOnStack();
		for (let i = 0; i < count; i++) {
			this.stateSystem.popState();
		}
		this.stateSystem.pushState(new ClientReconnectingState(this, hasConnection));
	}

	/** Several things can pause at once (pause button, settings menu, tiebreaker announcement), so it stays paused until
	 * each one that paused calls unpause() */
	pause() {
		this.setPaused(this.paused + 1);
	}

	unpause() {
		this.setPaused(Math.max(0, this.paused - 1)); //goToLobby clears every pause, so an unpause after it has nothing to undo
	}

	private setPaused(count: number) {
		this.paused = count;
		this.renderEngine.setPaused(count > 0);
		this.updateUI();
	}

	startEndlessMode() {
		const state = this.stateSystem.peekState();
		if (state.getName() !== "end") {
			console.error("Tried to start endless mode from non-end state");
			return
		}
		this.endlessMode = true;
		this.playTimer.resume(); //Paused on the end screen
		this.stateSystem.pushState(new ClientShopState(this));
		this.connectionToServer?.send({ messageType: ClientAction.StartEndlessMode });
	}

	screenShake(impact: number) {
		this.cameraSystem.impact(impact);
	}

	getMaxRounds() {
		return SimulationConstants.MaxRoundsByTrainingLevel[this.trainingLevel];
	}

	hasWon(player = this.getPlayer()): boolean {
		const hasWon = player && this.winners && this.winners.includes(player.id);
		return hasWon || false;
	}

	updateBattleRoundReward() {
		super.updateBattleRoundReward();
		if (this.roundReward !== null) {
			//Assumes two players here
			const self = this.getPlayer()!;
			const opponent = this.players.find(p => p.id !== self.id)!;
			const playerIsLeft = self.spawnPoint[0] < opponent.spawnPoint[0];
			this.roundRewardDisplay = {
				experience: this.roundReward.experience,
				earned: null,
				leftScore: 0,
				rightScore: 0,
				playerIsLeft
			};
		}
		else {
			this.roundRewardDisplay = null;
		}
	}

	claimBattleRoundReward(winner: Player): void {
		super.claimBattleRoundReward(winner);
		if (!this.roundRewardDisplay) return; //should always exist
		if (winner == this.getPlayer()) {
			console.log('battle reward claimed')
			this.roundRewardDisplay.earned = true;
		}
		else {
			this.roundRewardDisplay.earned = false;
		}
		this.updateUI();
	}

	private getBugReportContext() {
		return {
			gameVersion: getVersionString(),
			currentState: this.getCurrentState(),
			playerId: this.playerID,
			playerName: this.playerName,
			round: this.round,
			trainingLevel: this.trainingLevel,
			isRankedGame: this.isRankedGame,
			isElectron: this.isElectron,
			isSpawnGame: this.isSpawnGame,
			isConnected: this.isConnected,
			isConnecting: this.isConnecting,
			peerConnected: this.peerConnected,
			opponentDisconnected: this.opponentDisconnected,
			hostId: this.hostId,
			lobbyCode: this.signallingServerLobbyCode,
			connectionType: this.connectionToServer?.constructor?.name ?? null,
			playerCount: this.players.length,
			requiredPlayers: this.requiredPlayers,
			botCount: this.players.filter(p => p.isBot).length,
			inSimulation: this.inSimulation,
			paused: this.paused,
			endlessMode: this.endlessMode,
			farthestStepNumber: this.farthestStepNumber,
		};
	}
}

// DEBUG: simulate disconnect - delete after testing
document.addEventListener('keydown', (e) => {
	if (e.key === 'F9') {
		const game = (window as any).gameClient as GameClient | undefined;
		if (game) {
			console.log('DEBUG: Simulating disconnect');
			game.connectionToServer?.close();
			game.onServerConnectionLost();
		}
	}
});

function readSavedClientStats(saveName: string): { opponentRank: number | null, playTimeMs: number | null } | null {
	try {
		const save = JSON.parse(localStorage.getItem(saveName) ?? 'null');
		const number = (value: unknown) => typeof value === 'number' && isFinite(value) ? value : null;
		return { opponentRank: number(save?.opponentRank), playTimeMs: number(save?.playTimeMs) };
	}
	catch {
		return null;
	}
}
